import React from 'react';
import { Badge, Button, Select, Space, Tag, Typography } from 'antd';
import { ImportOutlined, PlusOutlined, PrinterOutlined } from '@ant-design/icons';
import { formatMoney } from '@/utils/format';

const { Text } = Typography;

export interface EpcEstimateCommandBarBcrNav {
  onBackTenders?: () => void;
  onClassicWorkspace?: () => void;
  onOpenPursuit?: () => void;
  /** Jump to Bid Control Room Proposal tab after prepare/certify. */
  onOpenProposal?: () => void;
  tenderTitle?: string;
  onBidContext?: () => void;
}

export interface EpcEstimateCommandBarProps {
  estimateNumber?: string;
  revisionLabel?: string;
  status?: string;
  currency?: string;
  revOptions: Array<{ value: string; label: string }>;
  revisionId?: string;
  onRevisionChange: (id: string) => void;
  tec?: number | null;
  coverage?: {
    pct_boq_with_cost?: number;
    boq_without_cost_count?: number;
    pct_costs_linked?: number;
    unlinked_cost_line_count?: number;
  } | null;
  blockerCount?: number;
  readOnly?: boolean;
  revisePending?: boolean;
  submitPending?: boolean;
  bcrNav?: EpcEstimateCommandBarBcrNav;
  onAddLine?: () => void;
  onImport?: () => void;
  onClearBoq?: () => void;
  clearBoqPending?: boolean;
  onSections?: () => void;
  onSummary?: () => void;
  onPlugLibrary?: () => void;
  /** Open Pricing Strategy drawer (markup / target margin / bid). */
  onPricing?: () => void;
  /** Open Indirects authoring drawer. */
  onIndirects?: () => void;
  /** Open Contingency drawer. */
  onContingency?: () => void;
  /** Prepare EPC commercial proposal from Approved estimate. */
  onPrepareProposal?: () => void;
  /** Open landscape estimate printout (BOQ, costs, indirects, contingency, pricing). */
  onPrint?: () => void;
  /** Open Approval Runtime panel for this revision. */
  onApproval?: () => void;
  /** Highlight Approval when pending action. */
  approvalPending?: boolean;
  sellPrice?: number | null;
  marginPct?: number | null;
  onRevise?: () => void;
  onSubmit?: () => void;
  onCoveragePillClick?: () => void;
  onBoqWithoutCostClick?: () => void;
  onDiagnostics?: () => void;
  /** Optional readiness chips for immersive chrome. */
  pricingReady?: boolean | null;
  indirectsCount?: number | null;
  proposalBasisStatus?: 'CERTIFIED' | 'STALE' | 'NOT_RECORDED' | null;
}

export const EpcEstimateCommandBar: React.FC<EpcEstimateCommandBarProps> = ({
  estimateNumber,
  revisionLabel,
  status,
  currency,
  revOptions,
  revisionId,
  onRevisionChange,
  tec,
  coverage,
  blockerCount = 0,
  readOnly,
  revisePending,
  submitPending,
  bcrNav,
  onAddLine,
  onImport,
  onClearBoq,
  clearBoqPending,
  onSections,
  onSummary,
  onPlugLibrary,
  onPricing,
  onIndirects,
  onContingency,
  onPrepareProposal,
  onPrint,
  onApproval,
  approvalPending,
  sellPrice,
  marginPct,
  onRevise,
  onSubmit,
  onCoveragePillClick,
  onBoqWithoutCostClick,
  onDiagnostics,
  pricingReady,
  indirectsCount,
  proposalBasisStatus,
}) => {
  const boqPct = coverage?.pct_boq_with_cost ?? 0;
  const withoutCost = coverage?.boq_without_cost_count ?? 0;

  const coverageLabel =
    withoutCost > 0
      ? `${boqPct}% BOQ w/ cost · ${withoutCost} lines`
      : `${boqPct}% BOQ w/ cost`;

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 8,
        rowGap: 8,
        padding: '8px 12px',
        borderBottom: '1px solid #e8e8e8',
        background: '#fff',
        position: 'relative',
        zIndex: 2,
        overflow: 'visible',
      }}
    >
      {bcrNav ? (
        <Space wrap size={4} style={{ maxWidth: '100%' }}>
          {bcrNav.onBackTenders ? (
            <Button size="small" onClick={bcrNav.onBackTenders}>
              Tenders
            </Button>
          ) : null}
          {bcrNav.onClassicWorkspace ? (
            <Button size="small" onClick={bcrNav.onClassicWorkspace}>
              Exit workbench
            </Button>
          ) : null}
          {bcrNav.onOpenPursuit ? (
            <Button size="small" onClick={bcrNav.onOpenPursuit}>
              Pursuit
            </Button>
          ) : null}
          {bcrNav.tenderTitle ? (
            <Text strong ellipsis style={{ maxWidth: 200 }}>
              {bcrNav.tenderTitle}
            </Text>
          ) : null}
          {bcrNav.onBidContext ? (
            <Button size="small" type="link" onClick={bcrNav.onBidContext}>
              Bid context
            </Button>
          ) : null}
          <Text type="secondary">|</Text>
        </Space>
      ) : null}

      <Text strong>{estimateNumber || 'Estimate'}</Text>
      {revisionLabel ? <Text type="secondary">{revisionLabel}</Text> : null}
      <Tag>{status || '—'}</Tag>
      {currency ? <Tag color="blue">{currency}</Tag> : null}
      <Select
        size="small"
        style={{ minWidth: 140 }}
        options={revOptions}
        value={revisionId}
        onChange={onRevisionChange}
        placeholder="Revision"
      />
      {tec != null ? (
        <Text type="secondary">
          Cost {formatMoney(tec, { display: 'plain', maximumFractionDigits: 0 })}
        </Text>
      ) : null}
      {sellPrice != null ? (
        <Text
          type="secondary"
          style={{ cursor: onPricing ? 'pointer' : undefined, textDecoration: onPricing ? 'underline' : undefined }}
          onClick={onPricing}
          title={onPricing ? 'Open Pricing Strategy' : undefined}
        >
          Sell {formatMoney(sellPrice, { display: 'plain', maximumFractionDigits: 0 })}
        </Text>
      ) : onPricing ? (
        <Button size="small" type="link" onClick={onPricing} style={{ padding: 0 }}>
          Set sell / margin
        </Button>
      ) : null}
      {marginPct != null ? (
        <Text
          type="secondary"
          style={{ cursor: onPricing ? 'pointer' : undefined, textDecoration: onPricing ? 'underline' : undefined }}
          onClick={onPricing}
          title={onPricing ? 'Open Pricing Strategy' : undefined}
        >
          Margin {marginPct}%
        </Text>
      ) : null}

      <Tag
        color={boqPct >= 90 ? 'green' : boqPct >= 50 ? 'orange' : 'red'}
        style={{ cursor: onCoveragePillClick || onBoqWithoutCostClick ? 'pointer' : undefined }}
        onClick={withoutCost > 0 ? onBoqWithoutCostClick : onCoveragePillClick}
      >
        {coverageLabel}
      </Tag>

      {pricingReady != null ? (
        <Tag color={pricingReady ? 'green' : 'orange'} title="Commercial pricing readiness">
          Pricing {pricingReady ? 'READY' : 'OPEN'}
        </Tag>
      ) : null}
      {indirectsCount != null ? (
        <Tag
          color={indirectsCount > 0 ? 'blue' : 'default'}
          style={{ cursor: onIndirects ? 'pointer' : undefined }}
          onClick={onIndirects}
          title="Indirect cost lines"
        >
          Indirects {indirectsCount}
        </Tag>
      ) : null}
      {proposalBasisStatus ? (
        <Tag
          color={
            proposalBasisStatus === 'CERTIFIED'
              ? 'green'
              : proposalBasisStatus === 'STALE'
                ? 'red'
                : 'orange'
          }
          title="EPC proposal estimate basis"
        >
          Basis {proposalBasisStatus}
        </Tag>
      ) : null}

      {blockerCount > 0 && onDiagnostics ? (
        <Badge count={blockerCount} size="small" overflowCount={99}>
          <Button size="small" danger onClick={onDiagnostics}>
            Blockers
          </Button>
        </Badge>
      ) : null}

      <div style={{ flex: '1 1 8px', minWidth: 8 }} />

      {!readOnly && onAddLine ? (
        <Button size="small" type="primary" icon={<PlusOutlined />} onClick={onAddLine}>
          Add line
        </Button>
      ) : null}
      {!readOnly && onImport ? (
        <Button size="small" icon={<ImportOutlined />} onClick={onImport}>
          Import
        </Button>
      ) : null}
      {!readOnly && onClearBoq ? (
        <Button size="small" danger loading={clearBoqPending} onClick={onClearBoq}>
          Clear BOQ
        </Button>
      ) : null}
      {!readOnly && onPlugLibrary ? (
        <Button size="small" onClick={onPlugLibrary}>
          Plug WBS Library
        </Button>
      ) : null}
      {onPricing ? (
        <Button size="small" type="primary" ghost onClick={onPricing}>
          Pricing
        </Button>
      ) : null}
      {onIndirects ? (
        <Button size="small" onClick={onIndirects}>
          Indirects
        </Button>
      ) : null}
      {onContingency ? (
        <Button size="small" onClick={onContingency}>
          Contingency
        </Button>
      ) : null}
      {onPrepareProposal ? (
        <Button size="small" onClick={onPrepareProposal}>
          Prepare proposal
        </Button>
      ) : null}
      {onPrint ? (
        <Button size="small" icon={<PrinterOutlined />} onClick={onPrint}>
          Print
        </Button>
      ) : null}
      {onApproval ? (
        <Button
          size="small"
          type={approvalPending ? 'primary' : 'default'}
          danger={approvalPending}
          onClick={onApproval}
        >
          {approvalPending ? 'Approve / Review' : 'Approval'}
        </Button>
      ) : null}
      {onSections ? (
        <Button size="small" onClick={onSections}>
          Sections
        </Button>
      ) : null}
      {onSummary ? (
        <Button size="small" onClick={onSummary}>
          Summary
        </Button>
      ) : null}
      {onRevise ? (
        <Button size="small" onClick={onRevise} loading={revisePending}>
          New revision
        </Button>
      ) : null}
      {!readOnly && onSubmit ? (
        <Button size="small" type="primary" onClick={onSubmit} loading={submitPending}>
          Submit
        </Button>
      ) : null}
    </div>
  );
};
